import axios, { AxiosInstance } from "axios";
import API from "../Utils/API/API";
import workingTemplateCommuteSettingsRoutes from "../routes/working-template-commute-settings-routes";
import {
  CommuteAutoCheckoutFlag,
  CommuteDay,
  CommuteUseFlag,
  WorkingTemplateCommuteSettings,
  WorkingTemplateCommuteSettingsApi,
  WorkingTemplateCommuteSettingsFailure,
  WorkingTemplateCommuteSettingsInput,
  WorkingTemplateCommuteSettingsSuccess
} from "./working-template-commute-settings-api.types";

export class WorkingTemplateCommuteSettingsApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly retryable: boolean,
    readonly status?: number,
    readonly networkUnknown = false
  ) {
    super(message);
    this.name = "WorkingTemplateCommuteSettingsApiError";
  }
}

const NEW_TIME_DEFAULTS = {
  checkinAdjustEndTime: "11:00",
  checkinFixedEndStartTime: "11:00",
  checkinFixedEndTime: "20:00",
  checkinLimitTime: "20:00"
} as const;

function invalidResponse(): WorkingTemplateCommuteSettingsApiError {
  return new WorkingTemplateCommuteSettingsApiError(
    "WORKING_TEMPLATE_COMMUTE_INVALID_RESPONSE",
    "서버의 출퇴근 설정 응답을 확인하지 못했습니다.",
    true
  );
}

function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw invalidResponse();
  }
  return value as Record<string, unknown>;
}

function integer(value: unknown): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value)) {
    throw invalidResponse();
  }
  return value;
}

function positiveInteger(value: unknown): number {
  const parsed = integer(value);
  if (parsed <= 0) throw invalidResponse();
  return parsed;
}

function useFlag(value: unknown): CommuteUseFlag {
  const parsed = integer(value);
  if (parsed !== 0 && parsed !== 1) throw invalidResponse();
  return parsed;
}

function autoCheckoutFlag(value: unknown): CommuteAutoCheckoutFlag {
  const parsed = integer(value);
  if (parsed !== 0 && parsed !== 1 && parsed !== 2) throw invalidResponse();
  return parsed;
}

function time(value: unknown, fallback?: string): string {
  if (value === null && fallback) return fallback;
  if (typeof value !== "string") throw invalidResponse();
  const match = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(value);
  if (!match || Number(match[1]) > 23 || Number(match[2]) > 59) {
    throw invalidResponse();
  }
  return `${match[1]}:${match[2]}`;
}

function days(value: unknown): CommuteDay[] {
  if (!Array.isArray(value)) throw invalidResponse();
  const parsed = value.map(day => {
    const result = integer(day);
    if (result < 1 || result > 7) throw invalidResponse();
    return result as CommuteDay;
  });
  if (new Set(parsed).size !== parsed.length) throw invalidResponse();
  return parsed;
}

function normalizeSettings(
  value: unknown,
  requestedId: number
): WorkingTemplateCommuteSettings {
  const source = record(value);
  const workingTemplateIdx = positiveInteger(source.workingTemplateIdx);
  if (workingTemplateIdx !== requestedId) throw invalidResponse();

  return {
    workingTemplateIdx,
    pcUse: useFlag(source.pcUse),
    appUse: useFlag(source.appUse),
    startWorkingMin: integer(source.startWorkingMin),
    endWorkingMin: integer(source.endWorkingMin),
    approvalDataType: useFlag(source.approvalDataType),
    dayOfWeek: days(source.dayOfWeek),
    unCheckoutAutoCheckoutFlag: autoCheckoutFlag(
      source.unCheckoutAutoCheckoutFlag
    ),
    unCheckoutAutoCheckoutTime: time(source.unCheckoutAutoCheckoutTime),
    checkInStartWorkTimeSetFlag: useFlag(source.checkInStartWorkTimeSetFlag),
    checkoutEndWorkTimeSetFlag: useFlag(source.checkoutEndWorkTimeSetFlag),
    checkinStartWorkTimeSetFlagAddMin: integer(
      source.checkinStartWorkTimeSetFlagAddMin
    ),
    updateEndWorkTimeOnlyFlag: useFlag(source.updateEndWorkTimeOnlyFlag),
    checkinAdjustUse: useFlag(source.checkinAdjustUse),
    checkinAdjustUnitMin: positiveInteger(source.checkinAdjustUnitMin),
    checkinAdjustEndTime: time(
      source.checkinAdjustEndTime,
      NEW_TIME_DEFAULTS.checkinAdjustEndTime
    ),
    checkinFixedEndUse: useFlag(source.checkinFixedEndUse),
    checkinFixedEndStartTime: time(
      source.checkinFixedEndStartTime,
      NEW_TIME_DEFAULTS.checkinFixedEndStartTime
    ),
    checkinFixedEndTime: time(
      source.checkinFixedEndTime,
      NEW_TIME_DEFAULTS.checkinFixedEndTime
    ),
    checkinLimitUse: useFlag(source.checkinLimitUse),
    checkinLimitTime: time(
      source.checkinLimitTime,
      NEW_TIME_DEFAULTS.checkinLimitTime
    )
  };
}

async function execute(
  requestedId: number,
  request: Promise<{ data: WorkingTemplateCommuteSettingsSuccess }>
): Promise<WorkingTemplateCommuteSettingsSuccess> {
  try {
    const response = await request;
    if (!response.data || response.data.ok !== true) throw invalidResponse();
    return {
      ...response.data,
      data: normalizeSettings(response.data.data, requestedId)
    };
  } catch (error) {
    if (error instanceof WorkingTemplateCommuteSettingsApiError) throw error;
    if (axios.isCancel(error)) {
      throw new WorkingTemplateCommuteSettingsApiError(
        "WORKING_TEMPLATE_COMMUTE_ABORTED",
        "출퇴근 설정 요청이 취소되었습니다.",
        false
      );
    }
    if (axios.isAxiosError(error) && error.response) {
      const failure = error.response.data as WorkingTemplateCommuteSettingsFailure;
      if (failure && failure.ok === false && typeof failure.code === "string") {
        throw new WorkingTemplateCommuteSettingsApiError(
          failure.code,
          failure.message,
          failure.retryable,
          error.response.status
        );
      }
    }
    throw new WorkingTemplateCommuteSettingsApiError(
      "WORKING_TEMPLATE_COMMUTE_NETWORK_UNKNOWN",
      "서버 응답을 확인하지 못했습니다.",
      true,
      undefined,
      true
    );
  }
}

export function createWorkingTemplateCommuteSettingsApi(
  client: AxiosInstance
): WorkingTemplateCommuteSettingsApi {
  return {
    get: (workingTemplateIdx, signal) => {
      const url = workingTemplateCommuteSettingsRoutes.settings(
        workingTemplateIdx
      );
      return execute(workingTemplateIdx, client.get(url, { signal }));
    },
    put: (workingTemplateIdx, request, signal) => {
      const url = workingTemplateCommuteSettingsRoutes.settings(
        workingTemplateIdx
      );
      return execute(
        workingTemplateIdx,
        client.put(url, request as WorkingTemplateCommuteSettingsInput, {
          signal
        })
      );
    }
  };
}

const workingTemplateCommuteSettingsApi =
  createWorkingTemplateCommuteSettingsApi(API());

export default workingTemplateCommuteSettingsApi;
export * from "./working-template-commute-settings-api.types";
